/**
 * HomeScreen.jsx
 * Main dashboard with REAL customer data
 */
import React, { useState, useEffect } from 'react';
import { Calendar, Wallet, Dumbbell, Trophy, Moon, ChevronRight, FileText, CreditCard, AlertCircle, Receipt, X, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Dynamic greeting based on time of day (uses i18n)
const getGreetingKey = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'home.good_morning';
  if (hour < 18) return 'home.good_afternoon';
  return 'home.good_evening';
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const days = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
  return `${days[date.getDay()]}, ${date.getDate()}.${date.getMonth() + 1}.`;
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
};

export const HomeScreen = ({ 
  customerName, 
  onNavigate, 
  points = 0, 
  creditBalance = 0,
  nextAppointment,
  packages = [],
  portalToken,
  pendingFeedback = [],
  onOpenFeedback
}) => {
  // Calculate active package progress
  const activePackage = packages.find(p => p.remaining > 0);
  
  // Open items state
  const [openItems, setOpenItems] = useState(null);
  const [dismissedOpenItems, setDismissedOpenItems] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (portalToken) loadOpenItems();
  }, [portalToken]);

  const loadOpenItems = async () => {
    try {
      const response = await axios.get(`${API}/portal/open-items`, {
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      setOpenItems(response.data);
    } catch (error) {
      // Endpoint might not exist or no open items
      setOpenItems(null);
    }
  };

  const hasOpenItems = openItems && !dismissedOpenItems && (
    (openItems.unpaid_invoices?.length > 0) || 
    (openItems.unbilled_appointments?.length > 0)
  );
  
  return (
    <div className="p-4 space-y-4">
      {/* Greeting - Premium StrongFit style matching web portal */}
      <div className="pt-2">
        <span 
          className="text-[10px] font-semibold uppercase tracking-[0.15em] inline-block pb-1 mb-2"
          style={{ color: '#C9A227', borderBottom: '1.5px solid #C9A227' }}
        >
          {t(getGreetingKey())}
        </span>
        <h1 className="text-white text-2xl font-bold" style={{ fontFamily: "'Outfit', sans-serif", textTransform: 'none' }}>
          Hallo, {customerName}
        </h1>
        <p className="text-zinc-500 text-xs mt-1">Dein taeglicher Motivationsschub wartet auf dich.</p>
      </div>

      {/* Open Items Banner */}
      {hasOpenItems && (
        <div className="bg-gradient-to-r from-red-500/20 to-orange-500/10 rounded-2xl p-4 border border-red-500/30" data-testid="open-items-banner">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-white text-sm font-medium">{t('home.open_items')}</p>
                {openItems.unpaid_invoices?.length > 0 && (
                  <p className="text-red-300 text-xs mt-0.5">
                    {t('home.open_invoices', { count: openItems.unpaid_invoices.length })}
                    {openItems.total_amount ? ` - CHF ${openItems.total_amount.toFixed(2)}` : ''}
                  </p>
                )}
              </div>
            </div>
            <button 
              onClick={() => setDismissedOpenItems(true)}
              className="p-1 text-zinc-500 hover:text-zinc-300"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <button
            onClick={() => onNavigate('invoices')}
            className="mt-3 w-full py-2 bg-red-500/20 border border-red-500/30 rounded-xl text-red-400 text-xs font-medium hover:bg-red-500/30 transition-colors"
            data-testid="open-items-view-btn"
          >
            {t('home.view_invoices')}
          </button>
        </div>
      )}

      {/* Pending Feedback Banner */}
      {pendingFeedback.length > 0 && (
        <button
          onClick={() => onOpenFeedback?.(pendingFeedback[0])}
          className="w-full bg-gradient-to-r from-yellow-500/15 to-amber-500/10 rounded-2xl p-4 border border-yellow-500/30 text-left"
          data-testid="pending-feedback-banner"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Star className="w-5 h-5 text-yellow-400" />
            </div>
            <div className="flex-1">
              <p className="text-white text-sm font-medium">{t('feedback.banner_title')}</p>
              <p className="text-yellow-400 text-xs mt-0.5">
                {pendingFeedback[0].service_name || 'Training'} — {t('feedback.banner_tap')}
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-yellow-500" />
          </div>
        </button>
      )}

      {/* TagesZitat */}
      <div className="rounded-2xl p-4" style={{ background: 'rgba(201,162,39,0.08)', border: '1px solid rgba(201,162,39,0.15)' }}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(201,162,39,0.15)' }}>
            <FileText className="w-5 h-5" style={{ color: '#C9A227' }} />
          </div>
          <div>
            <p className="text-zinc-500 text-[10px] font-semibold uppercase tracking-wider mb-1">Tageszitat</p>
            <p className="text-white text-sm italic">
              &ldquo;Der einzige Weg, grossartige Arbeit zu leisten, ist zu lieben, was du tust.&rdquo;
            </p>
            <p className="text-xs mt-2" style={{ color: '#C9A227' }}>— Steve Jobs</p>
          </div>
        </div>
      </div>

      {/* Next Appointment Card - ECHTE DATEN */}
      <button 
        onClick={() => onNavigate('bookings')}
        className="w-full rounded-2xl p-4 text-left"
        style={{ background: 'rgba(201,162,39,0.06)', border: '1px solid rgba(201,162,39,0.12)' }}
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'rgba(201,162,39,0.12)' }}>
            <Calendar className="w-6 h-6" style={{ color: '#C9A227' }} />
          </div>
          <div className="flex-1">
            <p className="text-zinc-500 text-[10px] font-semibold uppercase tracking-wider">Nächster Termin</p>
            {nextAppointment ? (
              <>
                <p className="text-white font-medium">{nextAppointment.service_name || 'Termin'}</p>
                <p className="text-sm" style={{ color: '#C9A227' }}>
                  {formatDate(nextAppointment.start_time)} um {formatTime(nextAppointment.start_time)}
                </p>
              </>
            ) : (
              <p className="text-zinc-500 text-sm">Keine Termine geplant</p>
            )}
          </div>
          <ChevronRight className="w-5 h-5 text-zinc-500" />
        </div>
      </button>

      {/* Active Packages Summary */}
      {packages.length > 0 && (
        <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="flex justify-between items-center mb-3">
            <span className="text-zinc-500 text-[10px] font-semibold uppercase tracking-wider">Aktive Pakete</span>
            <button onClick={() => onNavigate('account')} className="text-sm" style={{ color: '#C9A227' }}>
              Alle
            </button>
          </div>
          {packages.slice(0, 2).map((pkg, i) => (
            <div key={i} className="flex items-center gap-3 py-2 border-b border-zinc-800/50 last:border-0">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(201,162,39,0.12)' }}>
                <CreditCard className="w-5 h-5" style={{ color: '#C9A227' }} />
              </div>
              <div className="flex-1">
                <p className="text-white text-sm">{pkg.service_name || pkg.name}</p>
                <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden mt-1 w-full">
                  <div 
                    className="h-full rounded-full" 
                    style={{ width: `${(pkg.remaining / pkg.total_units) * 100}%`, backgroundColor: '#C9A227' }} 
                  />
                </div>
              </div>
              <span className="text-sm font-medium" style={{ color: '#C9A227' }}>{pkg.remaining}/{pkg.total_units}</span>
            </div>
          ))}
        </div>
      )}

      {/* Today's Activity Summary */}
      <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="flex justify-between items-center mb-4">
          <span className="text-zinc-500 text-[10px] font-semibold uppercase tracking-wider">Heute</span>
          <button 
            onClick={() => onNavigate('health')}
            className="text-sm"
            style={{ color: '#C9A227' }}
          >
            Health Connect
          </button>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center">
            <div className="w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-2" style={{ backgroundColor: 'rgba(201,162,39,0.12)' }}>
              <Dumbbell className="w-6 h-6" style={{ color: '#C9A227' }} />
            </div>
            <p className="text-white font-medium">8.542</p>
            <p className="text-zinc-500 text-xs">Schritte</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-2" style={{ backgroundColor: 'rgba(201,162,39,0.12)' }}>
              <Trophy className="w-6 h-6" style={{ color: '#C9A227' }} />
            </div>
            <p className="text-white font-medium">420</p>
            <p className="text-zinc-500 text-xs">kcal</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 mx-auto rounded-xl flex items-center justify-center mb-2" style={{ backgroundColor: 'rgba(201,162,39,0.12)' }}>
              <Moon className="w-6 h-6" style={{ color: '#C9A227' }} />
            </div>
            <p className="text-white font-medium">7.5h</p>
            <p className="text-zinc-500 text-xs">Schlaf</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeScreen;
